import React, { useEffect, useState } from "react";
import axios from "axios";
import Slider from "react-slick";
import ProductCard from "../components/Productcard";
import HeroSection from "../components/HeroSection";
import OverviewSection from "../components/OverviewSection";
import '../styles/Home.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

function Home() {
  const [popularProducts, setPopularProducts] = useState([]);
  const [error, setError] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [products, setProducts] = useState([]);

  const fetchPopularProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/products`);
      if (response.data?.products && Array.isArray(response.data.products)) {
        setPopularProducts(response.data.products);
      } else {
        setError("Invalid response from API.");
      }
    } catch (err) {
      setError("Failed to fetch popular products. Please try again later.");
    }
  };
useEffect(() => {
  const fetchProducts = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;

      const url =
        selectedCategory === "All"
          ? `${baseUrl}/api/products`
          : `${baseUrl}/api/products/category/${selectedCategory}`;

      const res = await axios.get(url);

      setProducts(res.data.products || []);
    } catch (error) {
      console.error("Error fetching products", error);
    }
  };

  fetchProducts();
}, [selectedCategory]);

  useEffect(() => {
    fetchPopularProducts();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    document.querySelectorAll(".fade-section").forEach((el) =>
      observer.observe(el)
    );

    return () => observer.disconnect();
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <>
      {/* ✅ SEO Meta Tags */}
      <Helmet>
        <title>Relison Pharma – Quality Medicines, Trusted Care</title>
        <meta
          name="description"
          content="Relison Pharma offers trusted pharmaceutical products with a commitment to innovation, quality, and affordability. Explore our top healthcare solutions."
        />
        <meta name="keywords" content="pharma, healthcare, medical marketing, relison, trusted medicines, affordable drugs" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Relison Pharma" />
        <link rel="canonical" href="https://relisonpharma.com/" />
        <meta property="og:title" content="Relison Pharma – Quality Medicines, Trusted Care" />
        <meta
          property="og:description"
          content="Discover 17 high-quality healthcare products by Relison Pharma. Innovation meets compassion."
        />
        <meta property="og:image" content="https://relisonpharma.com/images/relison-banner.png" />
        <meta property="og:url" content="https://relisonpharma.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Relison Pharma – Trusted Healthcare" />
        <meta name="twitter:description" content="Explore our product line rooted in innovation and care." />
        <meta name="twitter:image" content="https://relisonpharma.com/images/relison-banner.png" />
      </Helmet>

<HeroSection />

<hr className="hero-section-divider" />
<div className="cta-section">
          <h2>Explore Our Full Range</h2>
          <p>Discover our products tailored to meet modern health challenges. Visit our products page to learn more.</p>
          <Link to="/products" className="explore-button">
            View All Products
          </Link>
        </div>
      
      <div className="home-container">
        <div className="why-choose-section-cards fade-section">
          <h2>Why Choose Relison Pharma?</h2>
          <p className="why-subtitle">
            A pharmaceutical marketing and distribution company driven by
            quality, ethics, and long-term healthcare partnerships.
          </p>

          <div className="why-cards-container">
            <div
              className={`why-card ${activeCard === 0 ? "active" : ""}`}
              onClick={() => setActiveCard(activeCard === 0 ? null : 0)}
            >
              <h3>Strict Quality & Compliance</h3>
              <p>
                Every product is quality-checked and aligned with GMP,
                WHO-GMP, and GLP standards to ensure safety, purity,
                and consistent performance across markets.
              </p>
            </div>

            <div
              className={`why-card ${activeCard === 1 ? "active" : ""}`}
              onClick={() => setActiveCard(activeCard === 1 ? null : 1)}
            >
              <h3>Affordable & Ethical Healthcare</h3>
              <p>
                We maintain competitive pricing while following ethical
                business practices that build long-term trust with
                distributors, doctors, and partners.
              </p>
            </div>

            <div
              className={`why-card ${activeCard === 2 ? "active" : ""}`}
              onClick={() => setActiveCard(activeCard === 2 ? null : 2)}
            >
              <h3>Research-Led Innovation</h3>
              <p>
                Continuous research and innovation help us introduce
                next-generation pharmaceutical solutions that address
                evolving healthcare needs.
              </p>
            </div>

            <div
              className={`why-card ${activeCard === 3 ? "active" : ""}`}
              onClick={() => setActiveCard(activeCard === 3 ? null : 3)}
            >
              <h3>Customer-Centric Operations</h3>
              <p>
                Our business model prioritizes reliability, responsiveness,
                and strong relationships to support healthcare delivery
                across India.
              </p>
            </div>
          </div>
        </div>

        <hr className="section-divider" />
        {/* ================= PRODUCT CATEGORIES ================= */}

<div className="category-section fade-section">
  <h2>Product Categories</h2>
  <p className="category-subtitle">
    Explore our pharmaceutical range by dosage form
  </p>

  <div className="category-grid">
    {[
      { name: "All", value: "All", icon: "📦" },
      { name: "Tablets", value: "Tablets", icon: "🟣" },
      { name: "Capsules", value: "Capsules", icon: "💊" },
      { name: "Syrups", value: "Syrups", icon: "🧴" },
      { name: "SoftGel", value: "SoftGel", icon: "🧬" },
      { name: "Injectables", value: "Injectables", icon: "💉" },
    ].map((cat) => (
      <button
        key={cat.value}
        className={`category-card ${
          selectedCategory === cat.value ? "active" : ""
        }`}
        onClick={() => setSelectedCategory(cat.value)}
      >
        <span className="category-icon">{cat.icon}</span>
        <h3>{cat.name}</h3>
      </button>
    ))}
  </div>
</div>


       <div className="popular-products-section fade-section">
  <h2>
    {selectedCategory === "All"
      ? "Popular Products"
      : `${selectedCategory} Products`}
  </h2>

  {error && <p className="error-message">{error}</p>}

  {products.length > 0 ? (
    <Slider {...settings}>
      {products.map((product) => (
        <div key={product._id}>
          <ProductCard product={product} />
        </div>
      ))}
    </Slider>
  ) : (
    <p>No products found in this category.</p>
  )}
</div>
<hr className="section-divider" />

<div className="focus-section fade-section">
  <h2>Our Capabilities & Focus</h2>

  <p className="focus-intro">
    Relison Pharma operates with a clear focus on quality-driven pharmaceutical
    marketing, efficient distribution, and long-term healthcare partnerships.
  </p>

  <div className="focus-content">
    {/* LEFT */}
    <div className="focus-column">
      <img
        src={require("../assets/capability.webp")}
        alt="Pharmaceutical capabilities"
        className="focus-image"
      />

      <h3>What We Do</h3>
      <ul>
        <li>Pharmaceutical product marketing and promotion</li>
        <li>Nationwide distribution support</li>
        <li>Dosage-form based product portfolio</li>
        <li>Quality-compliant sourcing and packaging</li>
      </ul>
    </div>

    {/* RIGHT */}
    <div className="focus-column">
      <img
        src={require("../assets/focus.webp")}
        alt="Business focus and operations"
        className="focus-image"
      />

      <h3>Our Business Focus</h3>
      <ul>
        <li>Ethical and transparent operations</li>
        <li>Doctor and distributor relationship building</li>
        <li>Affordable and accessible healthcare</li>
        <li>Long-term sustainable growth</li>
      </ul>
    </div>
  </div>
</div>

        <hr className="section-divider" />

        {/* ✅ Overview Section */}
        <div className="company-overview-section">
          
          <OverviewSection />
        </div>

        {/* ✅ CTA Section */}
        
      </div> {/* ✅ Close .home-container */}
    </>
  );
}

export default Home;
